import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { Outlet, NavLink, Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { getLocale } from '../lib/format-utils';
import api, {
    getPendingTimesheets,
    getAdminExpenseReports,
    getAllVacationPlans,
    getOverdueStatus,
    getReopenRequests,
    getExpenseReopenRequests,
    getUnpaidSupplierInvoiceCount,
    getInboundDocumentCount,
    updateMyLanguage,
    changePassword,
} from '../lib/api';
import { Button } from './ui/button';
import { Sheet, SheetContent } from './ui/sheet';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Input } from './ui/input';
import { Label } from './ui/label';
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuLabel,
    DropdownMenuSeparator,
    DropdownMenuTrigger,
} from './ui/dropdown-menu';
import {
    LayoutDashboard,
    Calendar,
    FileText,
    FolderKanban,
    AlertCircle,
    Users,
    CheckSquare,
    FileSpreadsheet,
    LogOut,
    Receipt,
    DollarSign,
    Palmtree,
    Settings,
    Shield,
    Menu,
    CircleHelp,
    Building2,
    Globe,
    Lock,
    Tags,
    Bell,
    Link2,
    Banknote,
    ChevronDown,
    ClipboardList,
    Database,
    BarChart2,
    ReceiptText,
    Contact,
    ScrollText,
    Inbox,
    Send,
} from 'lucide-react';
import { toast } from 'sonner';
import HelpPanel from './help-panel';

// Derive initials from a full name string, e.g. "Aamid Shaik" → "AS"
function getInitials(name) {
    if (!name) return '?';
    const parts = name.trim().split(/\s+/);
    if (parts.length === 1) return parts[0][0].toUpperCase();
    return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}

// Restore the original super admin token and navigate back to /superadmin
function exitImpersonation() {
    const originalToken = localStorage.getItem('hislandtime.original_token');
    if (originalToken) {
        localStorage.setItem('token', originalToken);
        const originalUser = localStorage.getItem('hislandtime.original_user');
        if (originalUser) localStorage.setItem('user', originalUser);
        localStorage.removeItem('hislandtime.original_token');
        localStorage.removeItem('hislandtime.original_user');
    }
    window.location.href = '/superadmin';
}

const DashboardLayout = () => {
    const { t, i18n } = useTranslation();
    const { user, logout, isAdmin, isOwner, isAccountant, isSuperAdmin } = useAuth();
    // Impersonation is active when the original super admin token is stashed in localStorage
    const isImpersonating = !!localStorage.getItem('hislandtime.original_token');
    const navigate = useNavigate();
    const location = useLocation();
    const [mobileNavOpen, setMobileNavOpen] = useState(false);
    const [helpOpen, setHelpOpen] = useState(false);

    // Collapsible nav section state — all open by default
    const [adminOpen, setAdminOpen] = useState(true);
    const [dataOpen, setDataOpen] = useState(true);
    const [accountsOpen, setAccountsOpen] = useState(true);

    // Avatar blob URL — fetched with auth headers to avoid 401 on the img tag
    const [avatarBlobUrl, setAvatarBlobUrl] = useState(null);

    useEffect(() => {
        if (!user?.has_avatar) { setAvatarBlobUrl(null); return; }
        let objectUrl = null;
        api.get('/users/me/avatar', { responseType: 'blob' })
            .then(res => {
                objectUrl = URL.createObjectURL(res.data);
                setAvatarBlobUrl(objectUrl);
            })
            .catch(() => setAvatarBlobUrl(null));
        return () => { if (objectUrl) URL.revokeObjectURL(objectUrl); };
    }, [user?.id, user?.has_avatar]);

    // Password change dialog state
    const [pwDialogOpen, setPwDialogOpen] = useState(false);
    const [pwData, setPwData] = useState({ current_password: '', new_password: '', confirm_password: '' });
    const [pwLoading, setPwLoading] = useState(false);

    // Sidebar nav badges — counts of items waiting for the user's action.
    // Refetched on every route change so the badge decrements after the
    // admin acts on the corresponding queue page. Failures are silent
    // (badges fall back to 0) — they're a UX hint, not load-bearing.
    const [navBadges, setNavBadges] = useState({
        approveTimesheets: 0,
        approveExpenses: 0,
        approveVacations: 0,
        supplierInvoices: 0,
        inboundDocuments: 0,
        overdue: false,
    });

    const fetchNavBadges = useCallback(async () => {
        try {
            const personal = await getOverdueStatus().catch(() => ({ data: { overdue: false } }));
            // Unpaid supplier invoices badge — gated on the same condition that
            // shows the Accounts nav section (isAdmin || isAccountant || isOwner),
            // not just isAdmin. A failure degrades to 0 — badges are a UX hint,
            // not load-bearing, same as every other badge fetch here.
            const supplierInvoicesPromise = (isAdmin || isAccountant || isOwner)
                ? getUnpaidSupplierInvoiceCount().catch(() => ({ data: { unpaid: 0 } }))
                : Promise.resolve({ data: { unpaid: 0 } });
            // Inbound documents needing review — same gate and same
            // degrade-to-0-on-failure as the badge above; it sits on the same
            // Accounts nav item the count describes.
            const inboundDocumentsPromise = (isAdmin || isAccountant || isOwner)
                ? getInboundDocumentCount().catch(() => ({ data: { needs_review: 0 } }))
                : Promise.resolve({ data: { needs_review: 0 } });
            if (isAdmin) {
                const [tsRes, exRes, vacRes, tsReopenRes, exReopenRes, supInvRes, inboundRes] = await Promise.all([
                    getPendingTimesheets().catch(() => ({ data: [] })),
                    getAdminExpenseReports('submitted').catch(() => ({ data: [] })),
                    getAllVacationPlans('pending').catch(() => ({ data: [] })),
                    getReopenRequests().catch(() => ({ data: [] })),
                    getExpenseReopenRequests().catch(() => ({ data: [] })),
                    supplierInvoicesPromise,
                    inboundDocumentsPromise,
                ]);
                // Both endpoints already filter to status "pending" server-side for
                // admin callers, but filter again defensively here rather than trust
                // that — a pending reopen request is otherwise invisible outside the
                // review pages themselves (14.7 / KD-076).
                const tsReopenPending = (tsReopenRes.data || []).filter((r) => r.status === 'pending').length;
                const exReopenPending = (exReopenRes.data || []).filter((r) => r.status === 'pending').length;
                setNavBadges({
                    approveTimesheets: (tsRes.data?.length || 0) + tsReopenPending,
                    approveExpenses: (exRes.data?.length || 0) + exReopenPending,
                    approveVacations: vacRes.data?.length || 0,
                    supplierInvoices: supInvRes.data?.unpaid || 0,
                    inboundDocuments: inboundRes.data?.needs_review || 0,
                    overdue: personal.data?.overdue || false,
                });
            } else {
                const [supInvRes, inboundRes] = await Promise.all([supplierInvoicesPromise, inboundDocumentsPromise]);
                setNavBadges({
                    approveTimesheets: 0,
                    approveExpenses: 0,
                    approveVacations: 0,
                    supplierInvoices: supInvRes.data?.unpaid || 0,
                    inboundDocuments: inboundRes.data?.needs_review || 0,
                    overdue: personal.data?.overdue || false,
                });
            }
        } catch (e) {
            // Non-fatal — badges stay at last known values.
        }
    }, [isAdmin, isAccountant, isOwner]);

    useEffect(() => {
        fetchNavBadges();
    }, [fetchNavBadges, location.pathname]);

    const handleLogout = () => {
        logout();
        navigate('/login');
    };

    const handleLanguageChange = async (lang) => {
        try {
            i18n.changeLanguage(lang);
            localStorage.setItem('hislandtime.locale', lang);
            await updateMyLanguage(lang);
        } catch {
            // Non-fatal — language is already changed locally
        }
    };

    const handlePasswordChange = async (e) => {
        e.preventDefault();
        if (pwData.new_password !== pwData.confirm_password) {
            toast.error(t('profile.passwords_no_match'));
            return;
        }
        if (pwData.new_password.length < 6) {
            toast.error(t('profile.password_too_short'));
            return;
        }
        setPwLoading(true);
        try {
            await changePassword({
                current_password: pwData.current_password,
                new_password: pwData.new_password,
            });
            toast.success(t('profile.password_changed'));
            setPwData({ current_password: '', new_password: '', confirm_password: '' });
            setPwDialogOpen(false);
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Failed to change password');
        } finally {
            setPwLoading(false);
        }
    };

    const currentLang = i18n.language?.startsWith('sv') ? 'sv' : 'en';

    // User menu - available to all users (including admins)
    // Expenses hidden: its functions (Add / Submit / History) now live inside
    // /dashboard/log-reports (renamed "Reports", the LogReports page) — Phase 5
    // 5.53 fully retired the legacy /dashboard/expenses page (ExpensesPageV2
    // deleted; /expenses now redirects to /dashboard/log-reports).
    const userLinks = [
        { to: '/dashboard', label: t('nav.dashboard'), testId: 'dashboard', icon: LayoutDashboard, end: true },
        { to: '/dashboard/log-reports', label: t('nav.reports'), testId: 'reports', icon: Calendar, count: navBadges.overdue ? 1 : 0 },
        { to: '/dashboard/vacation-planner', label: t('nav.vacation'), testId: 'vacation', icon: Palmtree },
        { to: '/dashboard/profile', label: t('nav.records'), testId: 'profile', icon: FileText },
    ];

    // Admin menu — visibility is per-link based on the five-role hierarchy:
    //   super_admin > owner > admin > accountant > employee
    // Note isOwner implies isAdmin (owner and super_admin are both in the
    // isAdmin set) while isAccountant is exclusive — role === 'accountant'.
    //
    // adminLinks          — isAdmin (admin | owner | super_admin): approvals
    // dataLinks           — sidebar Data section, gated per row
    // settingsConfigLinks — top-bar config gear, gated per row
    const adminLinks = [
        { to: '/dashboard/approvals', label: t('nav.approve_timesheets'), testId: 'approve-timesheets', icon: CheckSquare, count: navBadges.approveTimesheets },
        { to: '/dashboard/expense-approvals', label: t('nav.approve_expenses'), testId: 'approve-expenses', icon: Receipt, count: navBadges.approveExpenses },
        { to: '/dashboard/vacation-calendar', label: t('nav.approve_vacations'), testId: 'approve-vacations', icon: Palmtree, count: navBadges.approveVacations },
    ];

    // Data section — the operational records people open daily.
    // Order: Projects → Clients → Employees.
    //
    // The heading renders for (isAdmin || isAccountant) but each row carries
    // its own gate, because the roles genuinely differ per row: Projects and
    // Employees are admin surfaces (/dashboard/projects and
    // /dashboard/employees are adminOnly routes), while Clients is
    // deliberately open to accountants as well — routes/clients.py uses
    // require_accountant_or_admin, the same guard as Supplier Invoices. An
    // accountant therefore sees a Data section containing Clients only, which
    // is exactly the access they had while that link lived under its own
    // "Business Partners" heading. `testId` is the full attribute value rather
    // than a suffix so every id survives the move unchanged.
    const dataLinks = [
        { to: '/dashboard/projects', label: t('nav.projects'), testId: 'nav-admin-settings-projects', icon: FolderKanban, show: isAdmin },
        { to: '/dashboard/clients', label: t('nav.clients'), testId: 'nav-clients', icon: Contact, show: isAdmin || isAccountant },
        { to: '/dashboard/employees', label: t('nav.employees'), testId: 'nav-admin-settings-employees', icon: Users, show: isAdmin },
    ].filter((link) => link.show);

    // Config group — set-once configuration, reached from the gear in the top
    // bar rather than the sidebar (each entry opens a focused solo view).
    //
    // The old per-item `ownerOnly` boolean is generalised into a `show`
    // predicate: the dropdown itself is no longer owner-only, so every row
    // must state its own audience. Cost Codes, Deviations, Payroll, Expense
    // Rules and Notifications all sit behind adminOnly routes; Fortnox
    // Mapping keeps the wider (admin | accountant | owner) audience the
    // sidebar link it replaces already had — the accountant's read-only
    // Fortnox access is deliberate. Integrations and Company stay owner-only.
    const settingsConfigLinks = [
        { to: '/dashboard/settings?tab=cost-codes&solo=true', label: t('nav.cost_codes'), testId: 'settings-cost-codes', icon: Tags, show: isAdmin },
        { to: '/dashboard/deviations', label: t('nav.deviations'), testId: 'settings-deviations', icon: AlertCircle, show: isAdmin },
        { to: '/dashboard/settings?tab=time-policy&solo=true', label: t('nav.payroll'), testId: 'settings-payroll', icon: Banknote, show: isAdmin },
        { to: '/dashboard/settings?tab=expense-rules&solo=true', label: t('nav.expense_rules'), testId: 'settings-expense-rules', icon: Receipt, show: isAdmin },
        { to: '/dashboard/settings?tab=notifications&solo=true', label: t('nav.notifications'), testId: 'settings-notifications', icon: Bell, show: isAdmin },
        { to: '/dashboard/settings?tab=fortnox-mapping&solo=true', label: t('nav.fortnox_mapping'), testId: 'settings-fortnox-mapping', icon: Link2, show: isAdmin || isAccountant || isOwner },
        { to: '/dashboard/integrations', label: t('nav.integrations'), testId: 'settings-integrations', icon: Link2, show: isOwner },
        { to: '/dashboard/company', label: t('nav.company'), testId: 'company', icon: Building2, show: isOwner },
    ].filter((link) => link.show);

    const NavContent = () => (
        <>
            {/* Navigation - scrollable */}
            <nav className="flex-1 py-4 overflow-y-auto">
                {/* User Menu Section — always open, no collapse */}
                <div className="px-4 mb-2">
                    <p className="text-[10px] uppercase tracking-widest text-muted-foreground font-medium">
                        {t('nav.my_menu')}
                    </p>
                </div>
                {userLinks.map((link) => (
                    <NavLink
                        key={link.to}
                        to={link.to}
                        end={link.end}
                        className={({ isActive }) =>
                            `sidebar-link ${isActive ? 'active' : ''}`
                        }
                        data-testid={`nav-${link.testId}`}
                        onClick={() => setMobileNavOpen(false)}
                    >
                        <link.icon className="w-4 h-4" strokeWidth={1.5} />
                        <span>{link.label}</span>
                        {link.count > 0 && (
                            <span
                                className="ml-auto px-1.5 min-w-[20px] h-5 inline-flex items-center justify-center text-[10px] font-mono font-bold bg-amber-500 text-white rounded"
                                data-testid={`nav-badge-${link.testId}`}
                                aria-label={t('nav.badge_count', { count: link.count })}
                            >
                                {link.count}
                            </span>
                        )}
                    </NavLink>
                ))}

                {/* Admin section — approvals only, collapsible */}
                {isAdmin && (
                    <>
                        <button
                            onClick={() => setAdminOpen(p => !p)}
                            className="px-4 mt-6 mb-2 pt-4 border-t border-border w-full flex items-center gap-1 text-left"
                            aria-expanded={adminOpen}
                        >
                            <ClipboardList className="w-3 h-3" />
                            <p className="text-[10px] uppercase tracking-widest text-muted-foreground font-medium flex-1">
                                {t('nav.section_admin')}
                            </p>
                            <ChevronDown className={`w-3 h-3 text-muted-foreground transition-transform ${adminOpen ? '' : '-rotate-90'}`} />
                        </button>

                        {adminOpen && (
                            <>
                                {adminLinks.map((link) => (
                                    <NavLink
                                        key={link.to}
                                        to={link.to}
                                        className={({ isActive }) =>
                                            `sidebar-link ${isActive ? 'active' : ''}`
                                        }
                                        data-testid={`nav-admin-${link.testId}`}
                                        onClick={() => setMobileNavOpen(false)}
                                    >
                                        <link.icon className="w-4 h-4" strokeWidth={1.5} />
                                        <span>{link.label}</span>
                                        {link.count > 0 && (
                                            <span
                                                className="ml-auto px-1.5 min-w-[20px] h-5 inline-flex items-center justify-center text-[10px] font-mono font-bold bg-amber-500 text-white rounded"
                                                data-testid={`nav-badge-${link.testId}`}
                                                aria-label={t('nav.badge_count', { count: link.count })}
                                            >
                                                {link.count}
                                            </span>
                                        )}
                                    </NavLink>
                                ))}
                            </>
                        )}
                    </>
                )}

                {/* Data section — Projects, Clients, Employees. Promoted out of
                    the isAdmin-only Admin section so an accountant can reach
                    Clients here (see dataLinks above for the per-row gating).
                    Rendered only when at least one row survives its own gate,
                    so no role ever sees an empty heading. */}
                {dataLinks.length > 0 && (
                    <>
                        <button
                            onClick={() => setDataOpen(p => !p)}
                            className="px-4 mt-6 mb-2 pt-4 border-t border-border w-full flex items-center gap-1 text-left"
                            aria-expanded={dataOpen}
                        >
                            <Database className="w-3 h-3" />
                            <p className="text-[10px] uppercase tracking-widest text-muted-foreground font-medium flex-1">
                                {t('nav.section_data')}
                            </p>
                            <ChevronDown className={`w-3 h-3 text-muted-foreground transition-transform ${dataOpen ? '' : '-rotate-90'}`} />
                        </button>

                        {dataOpen && dataLinks.map((link) => (
                            <NavLink
                                key={link.testId}
                                to={link.to}
                                className={({ isActive }) =>
                                    `sidebar-link ${isActive ? 'active' : ''}`
                                }
                                data-testid={link.testId}
                                onClick={() => setMobileNavOpen(false)}
                            >
                                <link.icon className="w-4 h-4" strokeWidth={1.5} />
                                <span>{link.label}</span>
                            </NavLink>
                        ))}
                    </>
                )}

                {/* Accounts section — reports and Fortnox mapping, collapsible */}
                {(isAdmin || isAccountant || isOwner) && (
                    <>
                        <button
                            onClick={() => setAccountsOpen(p => !p)}
                            className="px-4 mt-6 mb-2 pt-4 border-t border-border w-full flex items-center gap-1 text-left"
                            aria-expanded={accountsOpen}
                        >
                            <FileSpreadsheet className="w-3 h-3" />
                            <p className="text-[10px] uppercase tracking-widest text-muted-foreground font-medium flex-1">
                                {t('nav.section_accounts')}
                            </p>
                            <ChevronDown className={`w-3 h-3 text-muted-foreground transition-transform ${accountsOpen ? '' : '-rotate-90'}`} />
                        </button>

                        {accountsOpen && (
                            <>
                                {/* Customer invoices (Phase 21). Gated with the
                                    section itself (admin / accountant / owner)
                                    — the same guard the backend invoice routes
                                    use (require_accountant_or_admin). Ordered
                                    first, above Supplier Invoices, so the money
                                    we are OWED reads before the money we OWE. */}
                                <NavLink
                                    to="/dashboard/invoices"
                                    className={({ isActive }) =>
                                        `sidebar-link ${isActive ? 'active' : ''}`
                                    }
                                    data-testid="nav-admin-invoices"
                                    onClick={() => setMobileNavOpen(false)}
                                >
                                    <ScrollText className="w-4 h-4" strokeWidth={1.5} />
                                    <span>{t('nav.invoices')}</span>
                                </NavLink>
                                <NavLink
                                    to="/dashboard/supplier-invoices"
                                    className={({ isActive }) =>
                                        `sidebar-link ${isActive ? 'active' : ''}`
                                    }
                                    data-testid="nav-admin-supplier-invoices"
                                    onClick={() => setMobileNavOpen(false)}
                                >
                                    <ReceiptText className="w-4 h-4" strokeWidth={1.5} />
                                    <span>{t('nav.supplier_invoices')}</span>
                                    {navBadges.supplierInvoices > 0 && (
                                        <span
                                            className="ml-auto px-1.5 min-w-[20px] h-5 inline-flex items-center justify-center text-[10px] font-mono font-bold bg-amber-500 text-white rounded"
                                            data-testid="nav-badge-supplier-invoices"
                                            aria-label={t('nav.badge_count', { count: navBadges.supplierInvoices })}
                                        >
                                            {navBadges.supplierInvoices}
                                        </span>
                                    )}
                                </NavLink>
                                {/* Inbound documents (Phase 25.4) — mail that
                                    arrived at the invoice mailbox and is held
                                    until someone turns it into a supplier
                                    invoice. Directly BELOW Supplier Invoices
                                    because that is where it empties into, and
                                    gated with the section itself (the backend
                                    routes use require_accountant_or_admin). */}
                                <NavLink
                                    to="/dashboard/inbound-documents"
                                    className={({ isActive }) =>
                                        `sidebar-link ${isActive ? 'active' : ''}`
                                    }
                                    data-testid="nav-admin-inbound-documents"
                                    onClick={() => setMobileNavOpen(false)}
                                >
                                    <Inbox className="w-4 h-4" strokeWidth={1.5} />
                                    <span>{t('nav.inbound_documents')}</span>
                                    {navBadges.inboundDocuments > 0 && (
                                        <span
                                            className="ml-auto px-1.5 min-w-[20px] h-5 inline-flex items-center justify-center text-[10px] font-mono font-bold bg-amber-500 text-white rounded"
                                            data-testid="nav-badge-inbound-documents"
                                            aria-label={t('nav.badge_count', { count: navBadges.inboundDocuments })}
                                        >
                                            {navBadges.inboundDocuments}
                                        </span>
                                    )}
                                </NavLink>
                                <NavLink
                                    to="/dashboard/billing"
                                    className={({ isActive }) =>
                                        `sidebar-link ${isActive ? 'active' : ''}`
                                    }
                                    data-testid="nav-admin-billing-reports"
                                    onClick={() => setMobileNavOpen(false)}
                                >
                                    <DollarSign className="w-4 h-4" strokeWidth={1.5} />
                                    <span>{t('nav.billing_reports')}</span>
                                </NavLink>
                                <NavLink
                                    to="/dashboard/reports"
                                    className={({ isActive }) =>
                                        `sidebar-link ${isActive ? 'active' : ''}`
                                    }
                                    data-testid="nav-admin-salary-reports"
                                    onClick={() => setMobileNavOpen(false)}
                                >
                                    <FileSpreadsheet className="w-4 h-4" strokeWidth={1.5} />
                                    <span>{t('nav.salary_reports')}</span>
                                </NavLink>
                                <NavLink
                                    to="/dashboard/analytics"
                                    className={({ isActive }) =>
                                        `sidebar-link ${isActive ? 'active' : ''}`
                                    }
                                    data-testid="nav-analytics"
                                    onClick={() => setMobileNavOpen(false)}
                                >
                                    <BarChart2 className="w-4 h-4" strokeWidth={1.5} />
                                    <span>{t('nav.analytics')}</span>
                                </NavLink>
                                <NavLink
                                    to="/dashboard/monthly-reports"
                                    className={({ isActive }) =>
                                        `sidebar-link ${isActive ? 'active' : ''}`
                                    }
                                    data-testid="nav-monthly-reports"
                                    onClick={() => setMobileNavOpen(false)}
                                >
                                    <Send className="w-4 h-4" strokeWidth={1.5} />
                                    <span>{t('nav.monthly_reports')}</span>
                                </NavLink>
                                {/* Fortnox Mapping moved to the top-bar config
                                    gear — set-once configuration, not a daily
                                    surface. Its (admin | accountant | owner)
                                    audience is preserved there per-row. */}
                            </>
                        )}
                    </>
                )}

                {/* Configuration section removed from sidebar — accessible via the gear-wheel dropdown in the top bar (owner only) */}
            </nav>
        </>
    );

    return (
        <div className={`flex h-screen overflow-hidden${isImpersonating ? ' pt-10' : ''}`} data-testid="dashboard-layout">
            {/* Impersonation banner — fixed at top, visible on every page while impersonating */}
            {isImpersonating && (
                <div className="fixed top-0 left-0 right-0 z-50 bg-amber-500 text-white px-4 py-2 flex items-center justify-between text-sm font-medium">
                    <span>
                        {t('superadmin.impersonating_as', 'Testing as')}{' '}
                        <strong>{user?.name}</strong>{' '}
                        ({user?.role})
                        {' — '}{t('superadmin.test_flag_note', 'test account only, no real employee data')}
                    </span>
                    <button
                        onClick={exitImpersonation}
                        className="ml-4 px-3 py-1 bg-white text-amber-700 rounded text-xs font-bold hover:bg-amber-50"
                    >
                        {t('superadmin.exit_test_session', 'Exit test session')}
                    </button>
                </div>
            )}
            {/* Sidebar — desktop only (lg+) */}
            <aside className="sidebar hidden lg:flex flex-col h-screen" data-testid="sidebar">
                {/* Logo */}
                <div className="h-16 border-b border-border flex items-center px-6 flex-shrink-0">
                    <Link to="/dashboard"><img src={`${process.env.REACT_APP_BACKEND_URL}/api/company/logo`} onError={(e) => { e.currentTarget.src = '/hisland-logo.png'; e.currentTarget.onerror = null; }} alt="Company logo" className="" /></Link>
                </div>
                <NavContent />
            </aside>

            {/* Mobile nav drawer */}
            <Sheet open={mobileNavOpen} onOpenChange={setMobileNavOpen}>
                <SheetContent side="left" className="p-0 w-64 flex flex-col">
                    <div className="h-16 border-b border-border flex items-center px-6 pr-14 flex-shrink-0">
                        <Link to="/dashboard"><img src={`${process.env.REACT_APP_BACKEND_URL}/api/company/logo`} onError={(e) => { e.currentTarget.src = '/hisland-logo.png'; e.currentTarget.onerror = null; }} alt="Company logo" className="" /></Link>
                    </div>
                    <NavContent />
                </SheetContent>
            </Sheet>

            {/* Contextual help panel */}
            <HelpPanel open={helpOpen} onClose={() => setHelpOpen(false)} />

            {/* Password change dialog — shown only for non-Azure users */}
            <Dialog open={pwDialogOpen} onOpenChange={setPwDialogOpen}>
                <DialogContent className="sm:max-w-sm">
                    <DialogHeader>
                        <DialogTitle className="font-heading tracking-tight flex items-center gap-2">
                            <Lock className="w-4 h-4" strokeWidth={1.5} />
                            {t('profile.change_password_title')}
                        </DialogTitle>
                    </DialogHeader>
                    <form onSubmit={handlePasswordChange} className="space-y-4 pt-2">
                        <div>
                            <Label htmlFor="avatar-current" className="label-uppercase mb-2 block">
                                {t('profile.current_password_label')}
                            </Label>
                            <Input
                                id="avatar-current"
                                type="password"
                                value={pwData.current_password}
                                onChange={(e) => setPwData({ ...pwData, current_password: e.target.value })}
                                required
                            />
                        </div>
                        <div>
                            <Label htmlFor="avatar-new" className="label-uppercase mb-2 block">
                                {t('profile.new_password_label')}
                            </Label>
                            <Input
                                id="avatar-new"
                                type="password"
                                value={pwData.new_password}
                                onChange={(e) => setPwData({ ...pwData, new_password: e.target.value })}
                                required
                            />
                        </div>
                        <div>
                            <Label htmlFor="avatar-confirm" className="label-uppercase mb-2 block">
                                {t('profile.confirm_password_label')}
                            </Label>
                            <Input
                                id="avatar-confirm"
                                type="password"
                                value={pwData.confirm_password}
                                onChange={(e) => setPwData({ ...pwData, confirm_password: e.target.value })}
                                required
                            />
                        </div>
                        <div className="flex justify-end gap-2 pt-2">
                            <Button
                                type="button"
                                variant="outline"
                                onClick={() => setPwDialogOpen(false)}
                                className="uppercase tracking-wider text-xs"
                            >
                                {t('common.cancel')}
                            </Button>
                            <Button
                                type="submit"
                                className="uppercase tracking-wider text-xs"
                                disabled={pwLoading}
                            >
                                {pwLoading ? t('common.updating') : t('profile.update_password_button')}
                            </Button>
                        </div>
                    </form>
                </DialogContent>
            </Dialog>

            {/* Main content - scrollable */}
            <main className="flex-1 bg-muted/30 overflow-y-auto min-w-0">
                {/* Top bar */}
                <header className="h-16 border-b border-border bg-background/80 backdrop-blur-md sticky top-0 z-50 flex items-center justify-between px-4 md:px-8">
                    <div className="flex items-center gap-3">
                        {/* Hamburger — mobile only */}
                        <Button
                            variant="ghost"
                            size="icon"
                            className="lg:hidden"
                            onClick={() => setMobileNavOpen(true)}
                            aria-label="Open navigation"
                        >
                            <Menu className="w-5 h-5" />
                        </Button>
                        <Link to="/dashboard"><img src={`${process.env.REACT_APP_BACKEND_URL}/api/company/logo`} onError={(e) => { e.currentTarget.src = '/hisland-logo.png'; e.currentTarget.onerror = null; }} alt="Company logo" className="h-6 lg:hidden" /></Link>
                        <span className="text-xs text-muted-foreground uppercase tracking-widest hidden sm:inline">
                            {new Date().toLocaleDateString(getLocale(), {
                                weekday: 'long',
                                year: 'numeric',
                                month: 'long',
                                day: 'numeric'
                            })}
                        </span>
                    </div>
                    <div className="flex items-center gap-2">
                        {user?.flex_balance !== undefined && user?.role !== 'accountant' && (
                            <div className="flex items-center gap-2">
                                <span className="text-xs text-muted-foreground uppercase tracking-widest">{t('nav.flex_balance')}</span>
                                <span className={`font-mono font-bold ${user.flex_balance >= 0 ? 'flex-positive' : 'flex-negative'}`}>
                                    {user.flex_balance > 0 ? '+' : ''}{user.flex_balance}h
                                </span>
                            </div>
                        )}
                        {/* Configuration gear — dropdown of set-once config links.
                            Widened from owner-only to (isAdmin || isAccountant ||
                            isOwner) now that admin surfaces (Cost Codes,
                            Deviations) and the accountant's Fortnox Mapping live
                            here. `settingsConfigLinks` is already filtered by the
                            per-row `show` gate, so the length check keeps the
                            trigger from rendering for a role with nothing in it. */}
                        {(isAdmin || isAccountant || isOwner) && settingsConfigLinks.length > 0 && (
                            <DropdownMenu>
                                <DropdownMenuTrigger asChild>
                                    <button
                                        className="flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-semibold uppercase tracking-wider border transition-colors text-muted-foreground border-border hover:text-foreground hover:bg-accent"
                                        title={t('nav.section_config')}
                                        data-testid="topbar-config-btn"
                                    >
                                        <Settings className="w-3.5 h-3.5" strokeWidth={2} />
                                        <span className="hidden sm:inline">{t('nav.section_config')}</span>
                                    </button>
                                </DropdownMenuTrigger>
                                <DropdownMenuContent align="end" className="w-52">
                                    <DropdownMenuLabel className="text-xs text-muted-foreground uppercase tracking-wider font-normal">
                                        {t('nav.section_config')}
                                    </DropdownMenuLabel>
                                    <DropdownMenuSeparator />
                                    {settingsConfigLinks.map((link) => (
                                        <DropdownMenuItem key={link.testId} asChild>
                                            <Link
                                                to={link.to}
                                                className="flex items-center gap-2 cursor-pointer"
                                                data-testid={`topbar-config-${link.testId}`}
                                            >
                                                <link.icon className="w-4 h-4" strokeWidth={1.5} />
                                                {link.label}
                                            </Link>
                                        </DropdownMenuItem>
                                    ))}
                                </DropdownMenuContent>
                            </DropdownMenu>
                        )}

                        {/* Super admin button — top bar, visible to super_admin only */}
                        {isSuperAdmin && (
                            <NavLink
                                to="/superadmin"
                                className={({ isActive }) =>
                                    `flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-semibold uppercase tracking-wider border transition-colors ${
                                        isActive
                                            ? 'bg-violet-600 text-white border-violet-600'
                                            : 'text-violet-600 border-violet-300 hover:bg-violet-50'
                                    }`
                                }
                                data-testid="topbar-superadmin-link"
                            >
                                <Shield className="w-3.5 h-3.5" strokeWidth={2} />
                                <span className="hidden sm:inline">Platform</span>
                            </NavLink>
                        )}

                        <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => setHelpOpen(true)}
                            aria-label={t('nav.help_button_label')}
                        >
                            <CircleHelp className="w-5 h-5" strokeWidth={1.5} />
                        </Button>

                        {/* Avatar dropdown — larger (10×10) for better visibility */}
                        <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                                <button
                                    className="relative w-10 h-10 rounded-full focus:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 shrink-0 overflow-hidden"
                                    aria-label="Account menu"
                                    data-testid="avatar-menu-trigger"
                                >
                                    {/* Initials base layer — no border, clean background */}
                                    <span className="absolute inset-0 w-10 h-10 rounded-full bg-foreground text-background text-sm font-bold flex items-center justify-center select-none">
                                        {getInitials(user?.name)}
                                    </span>
                                    {/* Photo layer — rendered on top when blob URL available */}
                                    {avatarBlobUrl && (
                                        <img
                                            src={avatarBlobUrl}
                                            alt=""
                                            aria-hidden="true"
                                            className="absolute inset-0 w-10 h-10 rounded-full object-cover"
                                        />
                                    )}
                                </button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end" className="w-56">
                                {/* Identity block */}
                                <DropdownMenuLabel className="font-normal pb-1">
                                    <p className="font-medium text-sm leading-tight">{user?.name}</p>
                                    <p className="text-xs text-muted-foreground truncate mt-0.5">{user?.email}</p>
                                    <span className="inline-block mt-1.5 px-2 py-0.5 bg-foreground text-background text-[10px] uppercase tracking-wider">
                                        {user?.role}
                                    </span>
                                </DropdownMenuLabel>

                                <DropdownMenuSeparator />

                                {/* Language toggle */}
                                <div className="px-2 py-1.5 flex items-center gap-2">
                                    <Globe className="w-3.5 h-3.5 text-muted-foreground shrink-0" strokeWidth={1.5} />
                                    <span className="text-xs text-muted-foreground uppercase tracking-widest mr-auto">Lang</span>
                                    <div className="flex gap-1">
                                        <button
                                            onClick={() => handleLanguageChange('en')}
                                            className={`px-2 py-0.5 text-[11px] font-medium uppercase tracking-wider border transition-colors ${
                                                currentLang === 'en'
                                                    ? 'bg-foreground text-background border-foreground'
                                                    : 'bg-transparent text-muted-foreground border-border hover:border-foreground hover:text-foreground'
                                            }`}
                                            aria-label="Switch to English"
                                            aria-pressed={currentLang === 'en'}
                                        >
                                            EN
                                        </button>
                                        <button
                                            onClick={() => handleLanguageChange('sv')}
                                            className={`px-2 py-0.5 text-[11px] font-medium uppercase tracking-wider border transition-colors ${
                                                currentLang === 'sv'
                                                    ? 'bg-foreground text-background border-foreground'
                                                    : 'bg-transparent text-muted-foreground border-border hover:border-foreground hover:text-foreground'
                                            }`}
                                            aria-label="Switch to Swedish"
                                            aria-pressed={currentLang === 'sv'}
                                        >
                                            SV
                                        </button>
                                    </div>
                                </div>

                                <DropdownMenuSeparator />

                                {/* Change password — hidden for Azure SSO users */}
                                {!user?.azure_id && (
                                    <>
                                        <DropdownMenuItem
                                            onClick={() => setPwDialogOpen(true)}
                                            className="cursor-pointer"
                                            data-testid="avatar-change-password"
                                        >
                                            <Lock className="w-4 h-4 mr-2" strokeWidth={1.5} />
                                            {t('profile.change_password_title')}
                                        </DropdownMenuItem>
                                        <DropdownMenuSeparator />
                                    </>
                                )}

                                {/* Sign out */}
                                <DropdownMenuItem
                                    onClick={handleLogout}
                                    className="cursor-pointer text-destructive focus:text-destructive"
                                    data-testid="avatar-logout"
                                >
                                    <LogOut className="w-4 h-4 mr-2" strokeWidth={1.5} />
                                    {t('nav.logout')}
                                </DropdownMenuItem>
                            </DropdownMenuContent>
                        </DropdownMenu>
                    </div>
                </header>

                {/* Page content */}
                <div className="p-4 md:p-8">
                    <Outlet />
                </div>
            </main>
        </div>
    );
};

export default DashboardLayout;
